import * as jsondiffpatch from 'jsondiffpatch';
import * as jsonPatchFormatterRFC6902 from 'jsondiffpatch/formatters/jsonpatch';
import type {
  ChangelogInputBundle, ChangesByActionPublicId, Comparable, RFC6902Operation,
} from './types';
import { buildComparable } from './helpers';
import type { ChangelogFormatter } from './formatters';
import type { FeatureFlags } from '~/helpers/typescript';
import createOperationProcessors, { createChangesBuffer } from './operationProcessors';
import { executePredicatedProcessors } from './lib/predicatedProcessor';


/**
 * Generates a diff between comparable in RFC 6902 format
 * https://datatracker.ietf.org/doc/html/rfc6902
 *
 * @returns an array of changes between the versions in RFC 6902 format
 */
function generateRFC6902Operations(
  versionA: Comparable,
  versionB: Comparable) {
  const delta = jsondiffpatch.diff(versionA, versionB);

  return jsonPatchFormatterRFC6902.format(delta);
}


/**
 *
 * Produces an array of operations representing the changes between A and B
 */
export function generateOperations(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
}) {
  const { A, B } = args;

  const comparableA = buildComparable({
    compareInput: {
      version: A.version,
      workflowProps: A.props,
    },
    supplementalData: {
      initialWorkflowProps: A.props,
    },
  });

  const comparableB = buildComparable({
    compareInput: {
      version: B.version,
      workflowProps: B.props,
    },
    supplementalData: {
      initialWorkflowProps: A.props,
    },
  });

  const operations = generateRFC6902Operations(comparableA, comparableB);
  return operations;
}

/**
 * Processes a list of operations and creates Changes representing them.
 * @param operations operations that happened from versionA to versionB
 * @param versions a bundle of versions to compare
 * @returns a record of changes keyed by the action they belong to
 */
export function computeChangesPerAction(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  operations: RFC6902Operation[];
},
): ChangesByActionPublicId {
  const { operations, A, B } = args;
  const changesBuffer = createChangesBuffer();
  const operationProcessors = createOperationProcessors({ changesBuffer, A, B });
  executePredicatedProcessors({ items: operations, processorRegistry: operationProcessors });
  return changesBuffer.package();
}


export default function generateVersionChangelog<T>(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  formatter: ChangelogFormatter<T>;
  featureFlags: Partial<FeatureFlags>;
}) {
  const {
    A, B, formatter, featureFlags,
  } = args;
  const operations = generateOperations({ A, B });
  const changesByActionPublicId = computeChangesPerAction({ operations, A, B });

  return {
    content: formatter({
      changesByActionPublicId, A, B, featureFlags,
    }),
    changesCount: Object.keys(changesByActionPublicId).length,
  };
}
